import type { CallbackInterface } from 'recoil'

import { logAppError } from 'Helpers/logger'

import type { AsyncTaskState } from 'Hooks/useAsyncTask/types'

import {
  errorUpdater,
  getTaskDetails,
  pollingIterationUpdater,
} from 'Hooks/useAsyncTask/services'
import { asyncTaskState } from 'Hooks/useAsyncTask/state'

export const execTaskCallback =
  ({ set }: CallbackInterface) =>
  async (api: () => Promise<number>, key: string) => {
    set(asyncTaskState(key), (prev) => ({
      ...prev,
      loading: true,
    }))

    try {
      const idTask = await api()

      set(asyncTaskState(key), (prev) => ({
        ...prev,
        idTask,
        pollingIteration: 1,
      }))
    } catch (error) {
      logAppError({
        Component: 'useAsyncTask',
        Method: 'execTaskCallback',
        appError: error,
      })

      set<AsyncTaskState>(asyncTaskState(key), (prev) => ({
        ...prev,
        status: 'error',
        loading: false,
      }))
    }
  }

export const pollingCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (key: string) => {
    const { idTask } = await snapshot.getPromise(asyncTaskState(key))

    if (idTask !== undefined && idTask > 0) {
      try {
        const { Status, Metadata } = await getTaskDetails(idTask)

        if (Status === 'Completed') {
          set<AsyncTaskState>(asyncTaskState(key), (prev) => ({
            ...prev,
            idTask,
            pollingIteration: 0,
            metadata: Metadata,
            status: 'ready',
            loading: false,
          }))
        } else if (Status === 'Error' || Status === 'Aborted') {
          set(asyncTaskState(key), errorUpdater)
        } else {
          set(asyncTaskState(key), pollingIterationUpdater)
        }
      } catch (error) {
        logAppError({
          Component: 'useAsyncTask',
          Method: 'pollingCallback',
          appError: error,
        })

        set<AsyncTaskState>(asyncTaskState(key), errorUpdater)
      }
    }
  }

export const resetCallback =
  ({ reset }: CallbackInterface) =>
  async (key: string) => {
    reset(asyncTaskState(key))
  }
